/**
 * Per-decision-kind cost benchmark for /admin/agent-economics.
 *
 * Consumes /api/agent-economics/benchmark — shows tenant median
 * cost-per-step vs trimmed-fleet baseline per kind (llm/tool/embedding/
 * retrieval) with an "efficient / normal / expensive" verdict.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Gauge, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_META = {
  efficient: { cls: 'bg-emerald-500/15 text-emerald-300', Icon: TrendingDown },
  normal: { cls: 'bg-slate-500/15 text-slate-300', Icon: Minus },
  expensive: { cls: 'bg-rose-500/15 text-rose-300', Icon: TrendingUp },
};

export default function AgentBenchmarkTile() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/agent-economics/benchmark?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!loaded) {
    return <Card className="bg-slate-950 border-slate-800/60 animate-pulse" data-testid="agent-benchmark-loading"><CardContent className="p-6 h-32" /></Card>;
  }
  if (!data || (data.rows || []).length === 0) {
    return (
      <Card className="bg-slate-950 border-emerald-500/20" data-testid="agent-benchmark-empty">
        <CardContent className="p-5 text-center">
          <Gauge className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            No per-step cost data yet. Log <code className="text-cyan-300">/agent-economics/step</code> events with <code className="text-cyan-300">kind</code> (llm/tool/embedding/retrieval) and <code className="text-cyan-300">cost_usd</code> to surface decision-kind benchmarks.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-950 border-cyan-400/30" data-testid="agent-benchmark-tile">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Gauge className="w-4 h-4 text-cyan-300" />
            Per-decision-kind cost benchmark
            <Badge className="bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 text-[9px] font-mono tracking-widest">{data.window_days}D</Badge>
          </CardTitle>
          {data.summary?.expensive_kinds?.length > 0 && (
            <div className="text-xs text-rose-300">
              {data.summary.expensive_kinds.length} expensive kind{data.summary.expensive_kinds.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-cyan-300 border-b border-slate-800">
              <tr>
                <th className="text-left py-2">Kind</th>
                <th className="text-right">Steps</th>
                <th className="text-right">Tenant median</th>
                <th className="text-right">Trimmed baseline</th>
                <th className="text-right">vs baseline</th>
                <th className="text-right">Total cost</th>
                <th className="text-right">Verdict</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {data.rows.map((r, i) => {
                const m = VERDICT_META[r.verdict] || VERDICT_META.normal;
                const Icon = m.Icon;
                return (
                  <tr key={i} className="hover:bg-slate-800" data-testid={`bench-row-${r.kind}`}>
                    <td className="py-2 text-white font-medium capitalize">{r.kind}</td>
                    <td className="py-2 text-right text-slate-400">{r.step_count}</td>
                    <td className="py-2 text-right font-mono text-slate-300">${r.tenant_median_cost_per_step_usd}</td>
                    <td className="py-2 text-right font-mono text-slate-500">${r.fleet_baseline_cost_per_step_usd}</td>
                    <td className="py-2 text-right font-bold text-white">{r.multiplier_vs_baseline}×</td>
                    <td className="py-2 text-right font-mono text-slate-300">${r.total_cost_usd}</td>
                    <td className="py-2 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${m.cls}`}>
                        <Icon className="w-2.5 h-2.5" /> {r.verdict}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-slate-300 italic mt-3">
          Baseline = 20–80 percentile-trimmed median of THIS tenant's own steps per kind. ≤0.7× = efficient · 0.7–1.3× = normal · &gt;1.3× = expensive.
        </div>
      </CardContent>
    </Card>
  );
}
